import clamp from "lodash/clamp";
import { EventKey } from "PFTypes/event_key";

import {
  ArrowDirection,
  CellCoord,
  CellRange,
  DragAxis,
  NormalizedRange,
  TableBounds
} from "./interactive_table.types";

const ARROW_DELTA: Record<ArrowDirection, CellCoord> = {
  [EventKey.ArrowUp]: { rowIndex: -1, colIndex: 0 },
  [EventKey.ArrowDown]: { rowIndex: 1, colIndex: 0 },
  [EventKey.ArrowLeft]: { rowIndex: 0, colIndex: -1 },
  [EventKey.ArrowRight]: { rowIndex: 0, colIndex: 1 }
};

export const normalizedRange = (range: CellRange): NormalizedRange => ({
  minRow: Math.min(range.anchor.rowIndex, range.active.rowIndex),
  maxRow: Math.max(range.anchor.rowIndex, range.active.rowIndex),
  minCol: Math.min(range.anchor.colIndex, range.active.colIndex),
  maxCol: Math.max(range.anchor.colIndex, range.active.colIndex)
});

export const isCoordsEqual = (cellA: CellCoord, cellB: CellCoord): boolean =>
  cellA.rowIndex === cellB.rowIndex && cellA.colIndex === cellB.colIndex;

export const isCellInRange = (coord: CellCoord, range: NormalizedRange): boolean =>
  coord.rowIndex >= range.minRow &&
  coord.rowIndex <= range.maxRow &&
  coord.colIndex >= range.minCol &&
  coord.colIndex <= range.maxCol;

const clampToAxis = (anchor: CellCoord, target: CellCoord, axis: DragAxis): CellCoord => {
  if (axis === DragAxis.Horizontal) {
    return { ...target, rowIndex: anchor.rowIndex };
  }
  if (axis === DragAxis.Vertical) {
    return { ...target, colIndex: anchor.colIndex };
  }
  return target;
};

const clampToBounds = (coord: CellCoord, bounds: TableBounds): CellCoord => ({
  rowIndex: clamp(coord.rowIndex, 0, bounds.rowCount - 1),
  colIndex: clamp(coord.colIndex, 0, bounds.colCount - 1)
});

export const computeSetAnchor = (coord: CellCoord) => ({
  selectionRange: { anchor: coord, active: coord } as CellRange,
  fillRange: null,
  isDragging: false,
  isFillDragging: false,
  editModeCoord: null,
  editModeTrigger: null
});

export const computeStartFillDrag = (anchor: CellCoord) => ({
  fillRange: { anchor, active: anchor } as CellRange,
  isFillDragging: true,
  isDragging: false
});

export const computeExtendSelection = (current: CellRange, target: CellCoord, axis: DragAxis): CellRange => ({
  anchor: current.anchor,
  active: clampToAxis(current.anchor, target, axis)
});

export const computeExtendFillRange = (
  selectionRange: CellRange,
  target: CellCoord,
  axis: DragAxis
): CellRange => ({
  anchor: selectionRange.anchor,
  // fill range active must stay outside the selection range —
  // use the selection's anchor as the fill's anchor reference for axis clamping
  active: clampToAxis(selectionRange.anchor, target, axis)
});

export const computeMoveSelection = (
  current: CellRange,
  direction: ArrowDirection,
  shiftHeld: boolean,
  axis: DragAxis,
  bounds: TableBounds
): CellRange => {
  const delta = ARROW_DELTA[direction];

  if (shiftHeld) {
    const next = clampToBounds(
      {
        rowIndex: current.active.rowIndex + delta.rowIndex,
        colIndex: current.active.colIndex + delta.colIndex
      },
      bounds
    );
    return { anchor: current.anchor, active: clampToAxis(current.anchor, next, axis) };
  }

  const next = clampToBounds(
    {
      rowIndex: current.anchor.rowIndex + delta.rowIndex,
      colIndex: current.anchor.colIndex + delta.colIndex
    },
    bounds
  );
  return { anchor: next, active: next };
};

export const computeTabAdvance = (current: CellCoord, bounds: TableBounds): CellCoord => {
  const nextCol = current.colIndex + 1;
  if (nextCol < bounds.colCount) {
    return { rowIndex: current.rowIndex, colIndex: nextCol };
  }
  const nextRow = current.rowIndex + 1;
  if (nextRow < bounds.rowCount) {
    return { rowIndex: nextRow, colIndex: 0 };
  }

  return current;
};

export const computeTabReverse = (current: CellCoord, bounds: TableBounds): CellCoord => {
  const prevCol = current.colIndex - 1;
  if (prevCol >= 0) {
    return { rowIndex: current.rowIndex, colIndex: prevCol };
  }
  const prevRow = current.rowIndex - 1;
  if (prevRow >= 0) {
    return { rowIndex: prevRow, colIndex: bounds.colCount - 1 };
  }

  return current;
};

/**
 * Extracts a CellCoord from a mouse event.
 *
 * Uses `document.elementFromPoint` rather than `event.target` so that the
 * correct <td> is always found even when the cursor is over a child element
 * (input, text node, etc.) that does not change between adjacent cells during
 * a horizontal drag.  `event.target` stays fixed on whatever element received
 * the initial mousedown, causing horizontal drags to appear stuck.
 */
export const getCoordFromEvent = (event: React.MouseEvent | MouseEvent): CellCoord | null => {
  const el = document.elementFromPoint(event.clientX, event.clientY);
  const td = el?.closest("td") ?? null;
  if (!td) {
    return null;
  }
  const rowIndex = parseInt((td as HTMLElement).dataset.rowIndex ?? "", 10);
  const colIndex = parseInt((td as HTMLElement).dataset.colIndex ?? "", 10);
  if (isNaN(rowIndex) || isNaN(colIndex)) {
    return null;
  }
  return { rowIndex, colIndex };
};
